import React, { createContext, useContext, useMemo, useSyncExternalStore } from 'react';

type TCallback = () => void;
type TStoreApi<Store> = {
  get: () => Store;
  set: (value: Partial<Store>) => void;
  subscribe: (callback: () => void) => () => void;
};
export function createFastStore<Store>(initialValue: Store): TStoreApi<Store> {
  const store = { current: initialValue };
  const subscribers = { current: new Set<TCallback>() };

  return {
    get: () => store.current,
    set: (value: Partial<Store>) => {
      store.current = { ...store.current, ...value };
      subscribers.current.forEach((callback) => callback());
    },
    subscribe: (callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    },
  };
}

export function createFastContext<Store>(initialState: Store) {
  const StoreContext = createContext<TStoreApi<Store> | null>(null);

  type TProviderProps = {
    children: React.ReactNode;
    initialState?: Store;
  };
  function Provider({ children, initialState: overrideInitialState }: TProviderProps) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const value = useMemo(() => createFastStore(overrideInitialState || initialState), []);
    return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
  }

  function useStore<SelectorOutput>(
    selector: (store: Store) => SelectorOutput
  ): [SelectorOutput, (value: Partial<Store>) => void] {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error('Store not found');
    }

    const state = useSyncExternalStore(
      store.subscribe,
      () => selector(store.get()),
      () => selector(store.get() || initialState)
    );

    return [state, store.set];
  }

  function useStoreRef() {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error('Store not found');
    }
    return store;
  }

  return {
    Provider,
    useStore,
    useStoreRef,
  };
}
